import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, apiUpload } from "../api";
import { useAuth } from "../auth/AuthContext";

const AVATAR_COLORS = {
  crimson: "#e50914",
  amber: "#f59e0b",
  emerald: "#10b981",
  azure: "#3b82f6",
  violet: "#8b5cf6",
  rose: "#f43f5e",
  slate: "#64748b",
  cyan: "#06b6d4",
};

export function ProfileAvatar({ profile, size = 96, className = "" }) {
  const color = AVATAR_COLORS[profile?.avatar] || AVATAR_COLORS.crimson;
  const letter = (profile?.name || "?").trim().slice(0, 1).toUpperCase();
  const image = profile?.avatar_image || "";

  return (
    <div
      className={`profile-avatar ${image ? "has-image" : ""} ${className}`.trim()}
      style={{
        width: size,
        height: size,
        background: image ? "#111" : `linear-gradient(145deg, ${color}, #111)`,
        fontSize: size * 0.42,
      }}
      aria-hidden="true"
    >
      {image ? <img src={image} alt="" /> : letter}
    </div>
  );
}

export default function ProfilesPage() {
  const { token, user, profile, setProfile, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [profiles, setProfiles] = useState([]);
  const [avatars, setAvatars] = useState(Object.keys(AVATAR_COLORS));
  const [presets, setPresets] = useState([]);
  const [maxProfiles, setMaxProfiles] = useState(5);
  const [managing, setManaging] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    avatar: "crimson",
    avatar_image: null,
    is_kids: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const data = await api("/api/profiles", { token });
    setProfiles(data.profiles || []);
    setMaxProfiles(data.max_profiles || 5);
    if (data.avatars?.length) setAvatars(data.avatars);
    if (data.avatar_presets?.length) setPresets(data.avatar_presets);
    return data.profiles || [];
  }

  useEffect(() => {
    if (!token) return;
    load().catch((err) => setError(err.message));
  }, [token]);

  if (!token || !user) return <Navigate to="/login" replace />;

  function pick(p) {
    setProfile(p);
    navigate(p.is_kids ? "/kids" : "/", { replace: true });
  }

  function startCreate() {
    setEditing("new");
    setForm({
      name: "",
      avatar: avatars[profiles.length % avatars.length] || "crimson",
      avatar_image: null,
      is_kids: false,
    });
    setError("");
  }

  function startEdit(p) {
    setEditing(p.id);
    setForm({
      name: p.name || "",
      avatar: p.avatar || "crimson",
      avatar_image: p.avatar_image || null,
      is_kids: Boolean(p.is_kids),
    });
    setError("");
  }

  async function saveProfile(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = {
        name: form.name,
        avatar: form.avatar,
        avatar_image: form.avatar_image || null,
        is_kids: form.is_kids,
      };
      if (editing === "new") {
        await api("/api/profiles", {
          method: "POST",
          token,
          body,
        });
      } else {
        const updated = await api(`/api/profiles/${editing}`, {
          method: "PUT",
          token,
          body,
        });
        if (profile?.id === editing) setProfile(updated);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeProfile(id) {
    if (!window.confirm("¿Borrar este perfil y su progreso?")) return;
    setError("");
    try {
      await api(`/api/profiles/${id}`, { method: "DELETE", token });
      if (profile?.id === id) setProfile(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function onUploadFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (editing === "new") {
      setError("Guarda el perfil primero y luego sube una imagen.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const updated = await apiUpload(`/api/profiles/${editing}/avatar`, {
        file,
        token,
      });
      setForm((prev) => ({ ...prev, avatar_image: updated.avatar_image }));
      if (profile?.id === editing) setProfile(updated);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="profiles-screen">
      <div className="profiles-top profiles-anim profiles-anim-top">
        <p className="brand">Casasm</p>
        <button type="button" className="btn-ghost" onClick={logout}>
          Salir de {user.name || user.email}
        </button>
      </div>

      <div className="profiles-panel">
        <h1 key={managing ? "manage" : "who"} className="profiles-anim profiles-anim-title">
          {managing ? "Administrar perfiles" : "¿Quién está viendo?"}
        </h1>
        <p className="muted profiles-anim profiles-anim-sub">
          Cada perfil guarda su propio progreso de reproducción.
        </p>

        {error ? <p className="error">{error}</p> : null}

        <div className="profiles-grid">
          {profiles.map((p, index) => (
            <div
              key={p.id}
              className={`profile-card profiles-anim profiles-anim-card${p.is_system_kids ? " profile-card-kids" : ""}`}
              style={{ "--profile-i": index }}
            >
              <button
                type="button"
                className="profile-pick"
                onClick={() => (managing && !p.is_system_kids ? startEdit(p) : pick(p))}
              >
                <ProfileAvatar profile={p} size={112} />
                <strong>{p.name}</strong>
                {p.is_kids ? <span className="kids-pill">Casasm Kids</span> : null}
              </button>
              {managing && !p.is_system_kids ? (
                <button
                  type="button"
                  className="profile-delete"
                  onClick={() => removeProfile(p.id)}
                >
                  Borrar
                </button>
              ) : null}
            </div>
          ))}

          {managing && profiles.length < maxProfiles ? (
            <button
              type="button"
              className="profile-add profiles-anim profiles-anim-card"
              style={{ "--profile-i": profiles.length }}
              onClick={startCreate}
            >
              <span>+</span>
              Añadir perfil
            </button>
          ) : null}
        </div>

        {editing ? (
          <form className="profile-edit-form profiles-anim profiles-anim-form" onSubmit={saveProfile}>
            <h2>{editing === "new" ? "Nuevo perfil" : "Editar perfil"}</h2>
            <div className="profile-edit-preview">
              <ProfileAvatar
                profile={{
                  name: form.name || "?",
                  avatar: form.avatar,
                  avatar_image: form.avatar_image,
                }}
                size={88}
              />
            </div>
            <label>
              Nombre
              <input
                required
                maxLength={80}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>

            <p className="profile-edit-label">Imagen de perfil</p>
            <div className="avatar-image-picker" role="listbox" aria-label="Imágenes de avatar">
              <button
                type="button"
                className={`avatar-image-opt letter ${!form.avatar_image ? "active" : ""}`}
                onClick={() => setForm({ ...form, avatar_image: null })}
                title="Solo inicial"
              >
                {((form.name || "?").trim().slice(0, 1) || "?").toUpperCase()}
              </button>
              {presets.map((src) => (
                <button
                  key={src}
                  type="button"
                  className={`avatar-image-opt ${form.avatar_image === src ? "active" : ""}`}
                  onClick={() => setForm({ ...form, avatar_image: src })}
                  aria-label={`Avatar ${src}`}
                >
                  <img src={src} alt="" />
                </button>
              ))}
            </div>

            <div className="avatar-upload-row">
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={onUploadFile}
              />
              <button
                type="button"
                className="btn-ghost"
                disabled={busy || editing === "new"}
                onClick={() => fileRef.current?.click()}
              >
                Subir imagen
              </button>
              {editing === "new" ? (
                <span className="muted">Guarda el perfil para poder subir una foto.</span>
              ) : (
                <span className="muted">JPG, PNG, WEBP o GIF · máx. 2 MB</span>
              )}
            </div>

            <p className="profile-edit-label">Color de fondo</p>
            <div className="avatar-picker" role="listbox" aria-label="Color de avatar">
              {avatars.map((a) => (
                <button
                  key={a}
                  type="button"
                  className={`avatar-swatch ${form.avatar === a ? "active" : ""}`}
                  style={{ background: AVATAR_COLORS[a] || "#e50914" }}
                  onClick={() => setForm({ ...form, avatar: a, avatar_image: null })}
                  aria-label={a}
                />
              ))}
            </div>
            <label className="users-active-check">
              <input
                type="checkbox"
                checked={form.is_kids}
                onChange={(e) => setForm({ ...form, is_kids: e.target.checked })}
              />
              Perfil infantil
            </label>
            <div className="form-actions">
              <button type="submit" disabled={busy}>
                {busy ? "Guardando…" : "Guardar"}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>
                Cancelar
              </button>
            </div>
          </form>
        ) : null}

        <div className="profiles-actions profiles-anim profiles-anim-actions">
          <button
            type="button"
            className="btn-ghost profiles-manage"
            onClick={() => {
              setManaging((v) => !v);
              setEditing(null);
            }}
          >
            {managing ? "Listo" : "Administrar perfiles"}
          </button>
        </div>
      </div>
    </div>
  );
}
